import { useQuery } from '@tanstack/react-query';
import { CircleCheck, CircleDashed, CircleOff, RefreshCw } from 'lucide-react';
import type { PlatformServiceStatus } from '@barbeiropro/contracts';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/shared/page-header';
import { ErrorState, LoadingState } from '@/components/shared/states';
import { api } from '@/lib/api';
import { Card } from './shared';

const META = {
  configured: { label: 'Configurado', icon: CircleCheck, cls: 'text-primary' },
  not_configured: { label: 'Não configurado', icon: CircleDashed, cls: 'text-amber-ink' },
  unavailable: { label: 'Indisponível', icon: CircleOff, cls: 'text-muted-foreground' },
} as const;

export function ServicesPage() {
  const q = useQuery({ queryKey: ['platform', 'services'], queryFn: () => api.get<PlatformServiceStatus[]>('/platform/services') });
  return (
    <div>
      <PageHeader
        title="Serviços"
        description="Serviços globais da instalação e o que definir no ambiente para ativá-los. As barbearias só veem se o recurso está disponível, sem detalhes técnicos."
        actions={
          <Button variant="outline" size="sm" onClick={() => void q.refetch()} disabled={q.isFetching}>
            <RefreshCw className={q.isFetching ? 'animate-spin' : undefined} aria-hidden /> Verificar de novo
          </Button>
        }
      />
      {q.isLoading ? (
        <LoadingState />
      ) : q.error ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {q.data?.map((s) => {
            const m = META[s.status];
            return (
              <Card key={s.key}>
                <div className="flex items-start gap-3">
                  <m.icon className={`mt-0.5 size-5 shrink-0 ${m.cls}`} aria-hidden />
                  <div className="min-w-0 flex-1">
                    <h2 className="font-semibold">
                      {s.label} <span className={`ml-2 text-xs font-semibold ${m.cls}`}>{m.label}</span>
                    </h2>
                    <p className="mt-0.5 text-sm text-muted-foreground">{s.summary}</p>
                    {s.info.length > 0 && (
                      <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
                        {s.info.map((i) => (
                          <div key={i.label} className="contents">
                            <dt className="text-muted-foreground">{i.label}</dt>
                            <dd className="min-w-0 break-all">{i.value}</dd>
                          </div>
                        ))}
                      </dl>
                    )}
                    <ul className="mt-3 flex flex-wrap gap-2" aria-label="Variáveis de ambiente">
                      {s.variables.map((v) => (
                        <li key={v.name} className="rounded-md border px-2 py-0.5 font-mono text-xs">
                          {v.name} <span className={v.set ? 'text-primary' : 'text-muted-foreground'}>{v.set ? 'definida' : 'ausente'}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
